import { Button, Icon, IconButton, Stack, Typography } from '@mui/material';
import type { TimetableStructure } from '@schooly/api';
import type { SchoolYear } from '@schooly/api';
import {
  Attention2Icon,
  CalendarIcon,
  CrossIcon,
  DownloadIcon,
  GroupsIcon,
  LocationIcon,
  ModalContent,
  ModalFooter,
  ModalMain,
  ModalSmall,
  PublishIcon,
  Spin,
  TableTemplateEsPng,
  TableTemplatePng,
  TableTemplatePtPng,
  TimeIcon,
  WarningBlock,
} from '@schooly/style';
import { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { ModalHeaderV2 } from '../../../../components/uikit-components/Modal/ModalHeaderV2';

import type { ImportFileState } from './helpers/importFileReducer';
import { ImportFileCard } from './ImportFileCard';
import { ImportStepCard, TemplateField } from './ImportStepCard';

export const TABLE_TEMPLATE_IMG_BY_LOCALE: Record<string, string> = {
  es: TableTemplateEsPng,
  pt: TableTemplatePtPng,
};

const TEMPLATE_FIELDS = [
  {
    icon: <CalendarIcon />,
    titleId: 'school-timetables-Import-Day',
    descriptionId: 'school-timetables-Import-DayDescription',
  },
  {
    icon: <TimeIcon />,
    titleId: 'school-timetables-Import-Period',
    descriptionId: 'school-timetables-Import-PeriodDescription',
  },
  {
    icon: <GroupsIcon />,
    titleId: 'school-timetables-Import-Group',
    descriptionId: 'school-timetables-Import-GroupDescription',
  },
  {
    icon: <LocationIcon />,
    titleId: 'school-timetables-Import-Room',
    descriptionId: 'school-timetables-Import-RoomDescription',
  },
];

export interface ImportTimetableContentProps {
  structure: TimetableStructure;
  schoolYear: SchoolYear;
  ageGroupNames: string;
  fileState: ImportFileState;
  isValidating: boolean;
  onSetFile: (file: File) => void;
  onClearFile: () => void;
  onExportGroups: () => Promise<void>;
  onExportTemplate: () => void;
  onValidateImport: () => void;
  onClose: () => void;
}

export const ImportTimetableContent: FC<ImportTimetableContentProps> = ({
  structure,
  schoolYear,
  ageGroupNames,
  fileState,
  isValidating,
  onSetFile,
  onClearFile,
  onExportGroups,
  onExportTemplate,
  onValidateImport,
  onClose,
}) => {
  const { formatMessage, locale } = useIntl();
  const templateImg = TABLE_TEMPLATE_IMG_BY_LOCALE[locale] ?? TableTemplatePng;
  const fileData = fileState.status === 'success' ? fileState.file : undefined;
  const [isExportingGroups, setIsExportingGroups] = useState(false);

  const handleExportGroups = useCallback(async () => {
    setIsExportingGroups(true);
    try {
      await onExportGroups();
    } finally {
      setIsExportingGroups(false);
    }
  }, [onExportGroups]);

  return (
    <ModalSmall open onClose={onClose}>
      <ModalHeaderV2
        sx={{ paddingTop: 0, paddingBottom: 1.5, alignItems: 'center' }}
        active
        title={
          <Typography variant="h1" py={0.25}>
            {formatMessage({ id: 'school-timetables-ImportTimetable' })}
          </Typography>
        }
        subHeader={
          <Typography color="common.grey2" variant="h3">
            {formatMessage(
              { id: 'school-timetables-Import-ForStructure' },
              { structureName: structure.name, schoolYear: schoolYear.name },
            )}
            {ageGroupNames ? ` • ${ageGroupNames}` : ''}
          </Typography>
        }
      >
        <IconButton onClick={onClose} sx={{ mt: -0.75 }}>
          <CrossIcon />
        </IconButton>
      </ModalHeaderV2>

      <ModalMain>
        <ModalContent active>
          <Stack gap={2} minWidth={500}>
            <ImportStepCard
              step={1}
              titleId="school-timetables-Import-ExportGroupInfo"
              descriptionId="school-timetables-Import-ExportGroupInfoDescription"
            >
              <Button
                variant="outlined"
                disabled={isExportingGroups}
                startIcon={isExportingGroups ? <Spin /> : <DownloadIcon />}
                sx={{ alignSelf: 'flex-start', backgroundColor: 'background.paper', ml: 7 }}
                onClick={handleExportGroups}
              >
                {formatMessage({ id: 'school-timetables-Import-ExportGroups' })}
              </Button>
            </ImportStepCard>

            <ImportStepCard
              step={2}
              titleId="school-timetables-Import-TableTemplate"
              descriptionId="school-timetables-Import-TableTemplateDescription"
            >
              <WarningBlock>
                <Stack direction="row" gap={2} alignItems="center">
                  <Icon sx={{ color: 'background.paper', path: { color: 'common.orange' } }}>
                    <Attention2Icon />
                  </Icon>
                  <Typography variant="h3" color="inherit">
                    {formatMessage({ id: 'school-timetables-Import-ColumnOrderWarning' })}
                  </Typography>
                </Stack>
              </WarningBlock>
              <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
                <Stack gap={2}>
                  {TEMPLATE_FIELDS.map(({ icon, ...field }) => (
                    <TemplateField key={field.titleId} icon={<Icon>{icon}</Icon>} {...field} />
                  ))}
                </Stack>
                <img
                  src={templateImg}
                  alt={formatMessage({ id: 'school-timetables-Import-TableTemplate' })}
                  width={306}
                  height={306}
                />
              </Stack>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={{ alignSelf: 'flex-start', backgroundColor: 'background.paper', ml: 7 }}
                onClick={onExportTemplate}
              >
                {formatMessage({ id: 'school-timetables-Import-DownloadTemplate' })}
              </Button>
            </ImportStepCard>

            <ImportStepCard
              step={3}
              titleId="school-timetables-Import-UploadFile"
              descriptionId="school-timetables-Import-UploadFileDescription"
              sx={{
                '&:hover': {
                  '.fileName': { textDecoration: 'underline' },
                  '.downloadIcon, .deleteIcon': { visibility: 'visible' },
                },
              }}
            >
              <ImportFileCard
                onDrop={onSetFile}
                isLoading={fileState.status === 'loading'}
                onClear={onClearFile}
                fileData={fileData}
                error={fileState.status === 'error'}
              />
            </ImportStepCard>
          </Stack>
        </ModalContent>
      </ModalMain>

      <ModalFooter active>
        <Button
          disabled={isValidating}
          onClick={onValidateImport}
          startIcon={isValidating ? <Spin /> : <PublishIcon />}
          sx={(theme) => ({
            '& .svg-icon path:last-child': { fill: theme.palette.primary.main },
          })}
        >
          {formatMessage({ id: 'school-timetables-Import' })}
        </Button>
      </ModalFooter>
    </ModalSmall>
  );
};
